import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, unref as _unref, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "flex h-6 w-full items-center px-2"
}
const _hoisted_3 = { class: "truncate" }
const _hoisted_4 = {
  key: 0,
  class: "text-muted-foreground ml-auto text-[10px]"
}
const _hoisted_5 = { class: "text-muted-foreground p-2 text-xs" }

import { ChevronDown, X, LoaderCircle } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxLabel,
        ComboboxPortal,
        ComboboxVirtualizer,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxSeparator,
        ComboboxTrigger,
        ComboboxViewport,
        TagsInputInput,
        TagsInputItem,
        TagsInputItemDelete,
        TagsInputItemText,
        TagsInputRoot,
        useFilter,
    } from 'reka-ui';
    import { computed, ref } from 'vue';
    import type { SelectOption } from '@/types';
    import { useI18n } from 'vue-i18n';
    import { cn } from '@/lib/utils';

    interface Props {
        modelValue: string[] | number[];
        options: SelectOption[] | Record<string, any>[];
        placeholder?: string;
        translate?: boolean;
        loading?: boolean;
        grouped?: boolean;
        showAddon?: boolean;
        deletedIsSelectable?: boolean;
        labelBy?: string;
        valueBy?: string;
        disabled?: boolean;
        position?: 'inline' | 'popper';
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'MultiCombobox',
  props: {
    modelValue: {},
    options: {},
    placeholder: { default: '' },
    translate: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    grouped: { type: Boolean, default: false },
    showAddon: { type: Boolean, default: true },
    deletedIsSelectable: { type: Boolean, default: false },
    labelBy: { default: 'label' },
    valueBy: { default: 'value' },
    disabled: { type: Boolean },
    position: { default: 'popper' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emit = __emit;

    const { t } = useI18n();
    const { contains } = useFilter({ sensitivity: 'base' });

    const searchTerm = ref('');

    const values = computed({
        get: () => props.modelValue.map((value) => value.toString()),
        set: (newValues) => {
            emit('update:modelValue', newValues);
        },
    });

    const getOptionValue = (option: SelectOption | Record<string, any>): string => {
        return (option as Record<string, any>)[props.valueBy]?.toString() || '';
    };

    const getOptionLabel = (option: SelectOption | Record<string, any>): string => {
        const label = (option as Record<string, any>)[props.labelBy];
        return props.translate ? t(label) : label;
    };

    const translatedOptions = computed(() =>
        props.options.map((option) => ({
            ...option,
            value: getOptionValue(option),
            label: getOptionLabel(option),
            addon: option.addon,
            deleted_at: option.deleted_at,
            is_group: option.is_group,
            is_seperator: option.is_seperator,
        })),
    );

    const filteredOptions = computed(() =>
        translatedOptions.value.filter(
            (option) =>
                contains(option.label, searchTerm.value) && !values.value.includes(option.value),
        ),
    );

    const placeholderWithDefault = computed(
        () => props.placeholder || t('forms.select_placeholder'),
    );

    const selectedOptions = computed(() => {
        const map = new Map();
        values.value.forEach((value) => {
            const option = translatedOptions.value.find((opt) => opt.value === value);
            if (option) {
                map.set(value, option);
            }
        });
        return map;
    });

    const open = ref(false);
    const comboboxInputRef = ref<InstanceType<typeof ComboboxInput> | null>(null);

    const handleKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Tab') {
            open.value = false;
        }
    };

    const handleInteractOutside = (event: any) => {
        if (event.target !== comboboxInputRef.value?.$el) {
            open.value = false;
        }
    };

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ComboboxRoot), {
    modelValue: values.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((values).value = $event)),
    open: open.value,
    "onUpdate:open": _cache[5] || (_cache[5] = ($event: any) => ((open).value = $event)),
    multiple: true,
    "ignore-filter": "",
    class: "group relative",
    disabled: _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(ComboboxAnchor), { class: "group-data-[disabled]:bg-faded text-foreground data-[placeholder]:text-faded-foreground border-input flex min-h-7.5 w-full items-center justify-between gap-[5px] rounded-md border bg-white p-0.75 text-xs leading-none shadow-xs outline-none" }, {
        default: _withCtx(() => [
          _createVNode(_unref(TagsInputRoot), {
            modelValue: values.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((values).value = $event)),
            delimiter: "",
            class: "flex flex-1 flex-wrap items-center gap-1 rounded-lg"
          }, {
            default: _withCtx(() => [
              (!_ctx.loading)
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(values.value, (value) => {
                    return (_openBlock(), _createBlock(_unref(TagsInputItem), {
                      key: value,
                      value: value,
                      class: _normalizeClass(["bg-primary/10 aria-[current=true]:bg-primary text-foreground border-primary/20 flex items-center justify-center gap-2 rounded border px-1.5 py-0.5", {
                        'bg-destructive/10! border-destructive/20!':
                            selectedOptions.value.get(value)?.deleted_at,
                        'bg-accent/10! border-accent/20!':
                            selectedOptions.value.get(value)?.addon === 'userGroup',
                    }])
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(TagsInputItemText), { class: "text-xs" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(selectedOptions.value.get(value)?.label) + " ", 1),
                            (selectedOptions.value.get(value)?.deleted_at)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_1, " (" + _toDisplayString(_unref(t)('forms.is_deleted')) + ") ", 1))
                              : _createCommentVNode("", true)
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_unref(TagsInputItemDelete), null, {
                          default: _withCtx(() => [
                            _createVNode(_unref(X), { class: "size-3 cursor-pointer" })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1032, ["value", "class"]))
                  }), 128))
                : _createCommentVNode("", true),
              _createVNode(_unref(ComboboxInput), {
                modelValue: searchTerm.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchTerm).value = $event)),
                "as-child": "",
                onFocus: _cache[2] || (_cache[2] = 
                        () => {
                            open.value = true;
                        }
                    )
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(TagsInputInput), {
                    ref_key: "comboboxInputRef",
                    ref: comboboxInputRef,
                    placeholder: placeholderWithDefault.value,
                    class: "placeholder:text-faded-foreground h-5.5 flex-1 rounded !bg-transparent pl-1.75 focus:outline-none",
                    onKeydown: [
                      _cache[0] || (_cache[0] = _withKeys(_withModifiers(() => {}, ["prevent"]), ["enter"])),
                      handleKeydown
                    ]
                  }, null, 8, ["placeholder"])
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(ComboboxTrigger), { class: "shrink-0 pr-1.75" }, {
            default: _withCtx(() => [
              (!_ctx.loading)
                ? (_openBlock(), _createBlock(_unref(ChevronDown), {
                    key: 0,
                    class: "text-foreground/50 size-4 cursor-pointer"
                  }))
                : (_openBlock(), _createBlock(_unref(LoaderCircle), {
                    key: 1,
                    class: "text-foreground/50 ml-auto size-4 animate-spin"
                  }))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(ComboboxPortal), null, {
        default: _withCtx(() => [
          _createVNode(_unref(ComboboxContent), {
            class: _normalizeClass(
                    _unref(cn)(
                        'data-[state=open]:animate-in data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 mt-1 w-full overflow-hidden rounded-md border bg-white shadow-md will-change-[opacity,transform]',
                        _ctx.position === 'inline'
                            ? 'absolute max-h-60 w-full origin-top'
                            : 'max-h-(--reka-combobox-content-available-height) w-[var(--reka-popper-anchor-width)] origin-[var(--reka-popper-transform-origin)]',
                    )
                ),
            align: "start",
            "side-flip": "",
            "side-offset": 4,
            onPointerDownOutside: _withModifiers(handleInteractOutside, ["prevent"]),
            position: _ctx.position
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ComboboxViewport), {
                class: _normalizeClass(
                        _unref(cn)(
                            'overflow-y-auto p-1',
                            _ctx.position === 'inline' ? 'max-h-60' : 'max-h-(--reka-combobox-content-available-height)',
                        )
                    )
              }, {
                default: _withCtx(() => [
                  (!_ctx.grouped)
                    ? (_openBlock(), _createBlock(_unref(ComboboxVirtualizer), {
                        key: 0,
                        options: filteredOptions.value,
                        "text-content": (opt) => opt.label,
                        "estimate-size": 24
                      }, {
                        default: _withCtx(({ option }) => [
                          (option.is_group)
                            ? (_openBlock(), _createBlock(_unref(ComboboxLabel), {
                                key: option.label,
                                class: "text-foreground/50 mx-2 w-full px-0 py-1.5 text-xs leading-none"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(option.label), 1)
                                ]),
                                _: 2
                              }, 1024))
                            : (option.is_seperator)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                                  (_openBlock(), _createBlock(_unref(ComboboxSeparator), {
                                    class: "bg-border h-[1px] w-full",
                                    key: `${option.value}`
                                  }))
                                ]))
                              : (_openBlock(), _createBlock(_unref(ComboboxItem), {
                                  key: 2,
                                  class: "text-foreground data-[disabled]:text-muted-foreground data-[highlighted]:bg-primary/10 relative flex w-full cursor-pointer items-center px-2 py-1.5 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none",
                                  value: option.value
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("span", _hoisted_3, _toDisplayString(option.label), 1),
                                    (_ctx.showAddon && option.addon)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(option.addon), 1))
                                      : _createCommentVNode("", true)
                                  ]),
                                  _: 2
                                }, 1032, ["value"]))
                        ]),
                        _: 1
                      }, 8, ["options", "text-content"]))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(ComboboxEmpty), null, {
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('forms.no_options')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["class"])
            ]),
            _: 1
          }, 8, ["class", "position"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue", "open", "disabled"]))
}
}

})